// apps/client/src/ui-kit/PrizeModal.tsx
//
// Victory modal shown when the user has unclaimed prizes
// (e.g., Sunday Gala line/bingo win detected offline).
//
// Features:
// - Confetti-style animation via framer-motion
// - Shows prize amount + type
// - Dismiss button
//

import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './Button';

interface PendingPrize {
    id: string;
    type: string;
    amount: number;
    description: string | null;
    createdAt: string;
}

interface PrizeModalProps {
    prizes: PendingPrize[];
    onDismiss: () => void;
}

export function PrizeModal({ prizes, onDismiss }: PrizeModalProps) {
    if (prizes.length === 0) return null;

    const totalAmount = prizes.reduce((sum, p) => sum + p.amount, 0);
    const isSundayPrize = prizes.some((p) => p.type === 'SUNDAY_PRIZE');

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 flex items-center justify-center p-4"
                style={{ background: 'rgba(10, 5, 24, 0.95)' }}
            >
                {/* Confetti particles */}
                {[...Array(20)].map((_, i) => (
                    <motion.div
                        key={i}
                        initial={{
                            opacity: 0,
                            x: Math.random() * 400 - 200,
                            y: -100,
                            rotate: 0,
                            scale: 0,
                        }}
                        animate={{
                            opacity: [0, 1, 1, 0],
                            x: Math.random() * 400 - 200,
                            y: 600,
                            rotate: Math.random() * 720 - 360,
                            scale: [0, 1, 1, 0.5],
                        }}
                        transition={{
                            duration: 2 + Math.random() * 2,
                            delay: Math.random() * 0.5,
                            repeat: Infinity,
                            repeatDelay: Math.random() * 2,
                        }}
                        className="absolute"
                        style={{
                            width: 8 + Math.random() * 12,
                            height: 8 + Math.random() * 12,
                            borderRadius: Math.random() > 0.5 ? '50%' : '2px',
                            background: ['#FBBF24', '#F59E0B', '#EF4444', '#8B5CF6', '#10B981', '#3B82F6'][
                                Math.floor(Math.random() * 6)
                            ],
                        }}
                    />
                ))}

                <motion.div
                    initial={{ scale: 0.5, opacity: 0, y: 50 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    transition={{ type: 'spring', damping: 15, stiffness: 200, delay: 0.2 }}
                    className="relative bg-gradient-to-br from-[#1E1150] to-[#2D1B69] border border-[#FBBF24]/30 rounded-3xl p-8 max-w-sm w-full shadow-[0_0_60px_rgba(251,191,36,0.3)] text-center"
                >
                    {/* Trophy icon */}
                    <motion.div
                        animate={{ scale: [1, 1.1, 1], rotate: [0, 5, -5, 0] }}
                        transition={{ duration: 2, repeat: Infinity }}
                        className="text-7xl mb-4"
                    >
                        {isSundayPrize ? '🏆' : '🎉'}
                    </motion.div>

                    <h2 className="text-2xl font-black text-[#FBBF24] mb-2 uppercase tracking-wide">
                        You Won!
                    </h2>

                    <p className="text-white/60 text-sm mb-6">
                        {isSundayPrize
                            ? 'Your Sunday Grand Draw results are in!'
                            : 'Congratulations on your win!'}
                    </p>

                    <div className="bg-[#FBBF24]/10 border border-[#FBBF24]/20 rounded-2xl p-4 mb-6">
                        <p className="text-[#FBBF24]/60 text-xs font-bold uppercase tracking-wider mb-1">
                            Prize
                        </p>
                        <p className="text-4xl font-black text-[#FBBF24]">
                            +{totalAmount.toLocaleString()}
                        </p>
                        <p className="text-white/40 text-xs mt-1">BingoChips</p>
                    </div>

                    {prizes.length > 1 && (
                        <div className="space-y-2 mb-6">
                            {prizes.map((prize) => (
                                <div
                                    key={prize.id}
                                    className="flex items-center justify-between bg-white/5 rounded-xl px-3 py-2"
                                >
                                    <span className="text-white/60 text-xs">
                                        {prize.description || prize.type}
                                    </span>
                                    <span className="text-[#FBBF24] font-bold text-sm">
                                        +{prize.amount.toLocaleString()}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}

                    <Button onClick={onDismiss} fullWidth size="lg">
                        🎊 Awesome!
                    </Button>

                    <p className="text-white/30 text-[10px] mt-4">
                        Prize credited to your BingoChips balance
                    </p>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}
